<script setup lang="ts">
import type { Schemas, operations } from "#shopware";
import type { BoxLayout } from "@shopware/composables";
import { getTranslatedProperty } from '@shopware/helpers'

const props = defineProps<{
    listing: Schemas['ProductListingResult'],
    /**
     * Optional base route for pagination links
     * Default is current route with page param
     */
    paginationBaseRoute?: string,
    showTopPagination?: boolean,
    showBottomPagination?: boolean,
    /**
     * When true, pagination is only visible to screen readers (for infinite scroll)
     */
    srOnlyPagination?: boolean,
    availableLimits?: number[],
    showTopLimiter?: boolean,
    showBottomLimiter?: boolean,
    showTopSorter?: boolean,
    showBottomSorter?: boolean,
    defaultLimit?: number,
    defaultSorting?: string,
    layoutType?: BoxLayout,
}>()

// Default values
const defaultLimitValue = props.defaultLimit || 80
const defaultPage = 1
const defaultSortingValue = props.defaultSorting || 'name-asc'

const {
    getCurrentListing,
    getCurrentPage,
    getCurrentSortingOrder,
    getElements,
    getLimit,
    getTotal,
    getSortingOrders,
    getTotalPagesCount,
    search,
    loading,
    setInitialListing,
} = useCategoryListing()

const { withOnSalePostFilter } = useOnSaleFilter()

const listingError = ref(false)

const isValidListing = (listing: unknown): boolean =>
    !!listing && Array.isArray((listing as Schemas['ProductListingResult']).elements)

const runListingSearch = async (criteria: Record<string, unknown>) => {
    listingError.value = false
    try {
        await search(criteria as never)
    } catch {
        listingError.value = true
        return
    }

    // Detect truncated/incomplete store-api responses (occasional 200 with a cut-off body).
    // e.g.: for big limits the api, should be fixed in shopware (maybe memory problem)
    if (!isValidListing(getCurrentListing.value)) listingError.value = true
}

// !!! IMPORTANT !!! to provide data for listing relevant components like filter
setInitialListing(
    props?.listing as Schemas["ProductListingResult"],
);

const route = useRoute()
const router = useRouter()
const initialPath = ref(route.path)

// Get Nuxt app instance for GTM tracking
const nuxtApp = useNuxtApp()

/**
 * Update listing with all parameters
 */
const updateListing = async (params: {
    page?: number,
    limit?: number,
    order?: string
}) => {
    // Create query object with current values
    const query = { 
        ...route.query,
        // Update with new values, defaulting to current if not provided
        p: params.page?.toString() || getCurrentPage.value.toString(),
        limit: params.limit?.toString() || getLimit.value.toString(),
        order: params.order?.toString() || getCurrentSortingOrder.value?.toString()
    } as unknown as operations["searchPage post /search"]["body"];
    
    // Update URL (keep isOnSale in URL for persistence)
    await router.push({ query });

    // Apply to listing (strip isOnSale, inject proper filter)
    await runListingSearch(withOnSalePostFilter(query as unknown as Record<string, unknown>));
}

/**
 * Restore the listing on browser back/forward for same-path query changes, where
 * Vue Router reuses this mounted component so nothing else re-applies the URL.
 * popstate fires only for history back/forward — never for the controls' own
 */
let isBackForward = false
const onListingPopState = () => { isBackForward = true }

watch(() => route.query, () => {
    if (!isBackForward) return
    isBackForward = false
    if (Object.keys(route.query).length === 0) return
    runListingSearch(withOnSalePostFilter({ ...route.query } as Record<string, unknown>))
})

/**
 * Watch for route changes to handle navigation
 */
watch(
    () => route,
    (newRoute) => {
        // Only handle same-page navigation
        if (initialPath.value !== newRoute.path) {
            initialPath.value = newRoute.path;
            return;
        }
        
        // Reset to defaults if query is empty
        if (Object.keys(newRoute.query).length === 0) {
            updateListing({
                page: defaultPage,
                limit: defaultLimitValue,
                order: getCurrentSortingOrder.value || defaultSortingValue
            });
        }
    },
    { deep: true }
);

/**
 * Handle pagination change
 */
const onPageChange = async (page: number) => {
    await updateListing({ page });
    const listingElement = document.getElementById('product-listing')
    if (listingElement) listingElement.scrollIntoView({ behavior: 'smooth' })
};

const onLimitChange = async (limit: number) => {
    // Reset to page 1 when changing the limit
    await updateListing({ limit, page: 1 });
    const listingElement = document.getElementById('product-listing')
    if (listingElement) listingElement.scrollIntoView({ behavior: 'smooth' })
};

const onSortChange = async (order: string) => {
    // Reset to page 1 when changing the sorting
    await updateListing({ order, page: 1 });
};

/**
 * Generate base route for pagination that includes all current parameters
 */
const baseRoute = computed(() => {
    if (props.paginationBaseRoute) return props.paginationBaseRoute;
    
    // Use current route with page parameter and preserve other parameters
    const currentPath = route.path;
    const params = new URLSearchParams();
    
    params.append('p', ':page');
    
    if (getLimit.value) {
        params.append('limit', getLimit.value.toString());
    }
    
    if (getCurrentSortingOrder.value) {
        params.append('order', getCurrentSortingOrder.value);
    }
    
    // Add any other query parameters from the current route
    // except for pagination-related ones we're already handling
    Object.entries(route.query).forEach(([key, value]) => {
        if (key !== 'p' && key !== 'limit' && key !== 'order') {
            params.append(key, value as string);
        }
    });
 
    return `${currentPath}?${params.toString()}`;
});

const { t } = useI18n()

const productCount = computed(() => {
    const count = getTotal.value
    if (count === 0) return ''
    if (count === 1) return `${count} ${t('listing.product.singleCount')}`
    return `${count} ${t('listing.product.multipleCount')}`
})

const { category } = useCategory()

// Restore listing state from the URL on browser back/forward (see onListingPopState)
onMounted(() => window.addEventListener('popstate', onListingPopState))
onBeforeUnmount(() => window.removeEventListener('popstate', onListingPopState))

// Track category loaded (page view with customer context)
onMounted(() => {
    if (category.value) {
        const categoryName = getTranslatedProperty(category.value, 'name') || 'Product Listing'

        // Build breadcrumbs from category (breadcrumb is an array of strings)
        // Build cumulative URL path for each breadcrumb level
        const breadcrumbs = category.value.breadcrumb?.map((breadcrumbName: string, index: number) => {
            // First breadcrumb (e.g., "GISY") should link to homepage
            if (index === 0) {
                return {
                    name: breadcrumbName,
                    url: '/'
                }
            }

            // For subsequent breadcrumbs, build URL from second item onwards
            const urlParts = category.value.breadcrumb?.slice(1, index + 1) || []
            const url = '/' + urlParts.join('/').toLowerCase().replace(/\s+/g, '-')

            return {
                name: breadcrumbName,
                url: url
            }
        }) || []

        nuxtApp.callHook('nuxtware:category:loaded', {
            pageType: 'category',
            pageTitle: categoryName,
            breadcrumbs: breadcrumbs
        })
    }
})

// Track product impressions when products are loaded
watch(() => getElements.value, (products) => {
    if (products && products.length > 0 && import.meta.client) {
        const listName = getTranslatedProperty(category.value, 'name') || 'Product Listing'

        const gtmProducts = products.map((product: Schemas['Product'], index: number) => ({
            id: product.id,
            name: getTranslatedProperty(product, 'name') || '',
            price: product.calculatedPrice?.unitPrice || 0,
            brand: product.manufacturer?.name,
            category: product.categories?.[0]?.name,
            variant: product.options?.map((o: Schemas['PropertyGroupOption']) => o.name).join(' / '),
            position: index + 1
        }))

        nuxtApp.callHook('nuxtware:productListing:viewed', {
            products: gtmProducts,
            listName: listName
        })
    }
}, { immediate: true })

const headline = computed(() => {
    const customFields = category.value?.customFields as Record<string, unknown> | undefined
    if (customFields?.category_seo_h1_tag) {
        return customFields?.category_seo_h1_tag
    }

    return getTranslatedProperty(category.value, 'name')
})
</script>

<template>
    <div id="product-listing">
        <div class="mb-5 pb-5 px-2 mt-4 md:mb-0 md:mt-0 md:pb-0 md:px-0 xl:my-0 -mx-2 md:mx-0 w-[calc(100%+16px)] md:w-full border-b border-border md:border-0">
            <FoundationHeadline tag="h1" class="h1 font-bold uppercase md:inline-block md:me-3">{{ headline }}</FoundationHeadline>
            <span v-if="productCount" class="text-base-600 text-xs">{{ productCount }}</span>
        </div>
        <div class="flex flex-wrap items-center justify-end mb-3">
            <div class="w-full md:w-auto flex flex-wrap items-center justify-between md:justify-start gap-3 lg:gap-5">
                <div id="TeleportFilter" class="w-full md:w-auto">
                    <ClientOnly>
                        <template #fallback>
                            <div class="btn btn-medium btn-primary w-full md:w-45 flex justify-between items-center pointer-events-none animate-pulse">
                                <FoundationIcon
                                    name="filter"
                                    class="block w-5 h-5 opacity-30"
                                    size="md"
                                    aria-hidden="true" />
                                <span class="block ps-5 mx-auto opacity-30">{{ $t('listing.filter.title') }}</span>
                            </div>
                        </template>
                    </ClientOnly>
                </div>

                <ProductListingLimiter
                    v-if="showTopLimiter !== false"
                    instance-id="product-listing-limiter-top"
                    class="w-auto"
                    :current-limit="getLimit"
                    :available-limits="availableLimits"
                    @update:limit="onLimitChange"
                />

                <ProductListingSorter
                    v-if="showTopSorter !== false && getSortingOrders?.length"
                    instance-id="product-listing-sorter-top"
                    class="w-auto"
                    :current-sorting="getCurrentSortingOrder"
                    :available-sortings="getSortingOrders"
                    @update:sorting="onSortChange"
                />
            </div>
            
            <ProductListingPagination
                v-if="getTotalPagesCount > 1 && showTopPagination !== false"
                instance-id="product-listing-pagination-top"
                class="w-full md:w-auto mt-2 md:mt-0"
                :current-page="getCurrentPage"
                :total-pages="getTotalPagesCount"
                :base-route="baseRoute"
                :sr-only="srOnlyPagination"
                @page-change="onPageChange"
            />
        </div>

        <ProductActiveFilter class="hidden lg:flex flex-wrap items-center gap-2 mb-4" />

        <!-- Product grid -->
        <div v-if="!loading && !listingError && getElements.length > 0" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            <ProductCard
                v-for="(element, index) in getElements"
                :key="element.id"
                :product="element"
                :layout-type="layoutType"
                :position="index + 1"
                :list-name="getTranslatedProperty(category, 'name') || 'Product Listing'"
                :prioritize-first="true"
                image-sizes="272px sm:336px md:296px lg:258px xl:322px 2xl:345px"
            />
        </div>

        <!-- Error state (truncated/incomplete response) -->
        <div v-else-if="!loading && listingError" class="flex flex-col items-center justify-center py-16">
            <p class="text-base text-center">{{ t('listing.error.loadFailed') }}</p>
        </div>

        <!-- Empty state -->
        <div v-else-if="!loading && !listingError && getElements.length === 0" class="flex flex-col items-center justify-center py-16">
            <p class="text-base text-center mb-6">{{ t('listing.empty.noResults') }}</p>
            <FoundationButton
                color="primary"
                @click="router.push(route.path)"
            >
                {{ t('listing.empty.back') }}
            </FoundationButton>
        </div>
        <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 lg:gap-3">
            <ComponentSkeleton
                v-for="i in 8"
                :key="`skeleton-${i}`"
                preset="card"
                height="400px"
                class="border-b border-border"
            />
        </div>

        <FoundationButton 
            color="secondary" 
            class="flex m-auto md:ms-auto md:me-0 mb-3 mt-20"
            @click="onLimitChange(500)"
        >
            {{ t('listing.filter.showAll') }}
        </FoundationButton>

        <div class="flex flex-wrap items-center justify-between">
            <div class="w-full md:w-auto flex flex-wrap items-center justify-between md:justify-start gap-4">
                <ProductListingSorter
                    v-if="showBottomSorter !== false && getSortingOrders?.length"
                    instance-id="product-listing-sorter-bottom"
                    class="w-full"
                    :current-sorting="getCurrentSortingOrder"
                    :available-sortings="getSortingOrders"
                    @update:sorting="onSortChange"
                />
                
                <ProductListingLimiter
                    v-if="showBottomLimiter !== false"
                    instance-id="product-listing-limiter-bottom"
                    class="w-full"
                    :current-limit="getLimit"
                    :available-limits="availableLimits"
                    @update:limit="onLimitChange"
                />
            </div>

            <ProductListingPagination
                v-if="getTotalPagesCount > 1 && showBottomPagination !== false"
                instance-id="product-listing-pagination-bottom"
                class="w-full md:w-auto mt-2 md:mt-0 mb-10"
                :current-page="getCurrentPage"
                :total-pages="getTotalPagesCount"
                :base-route="baseRoute"
                :sr-only="srOnlyPagination"
                @page-change="onPageChange"
            />
        </div>
    </div>
</template>
